import React, { useState, useRef, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonInput,
  IonLabel,
  IonItem,
  IonList,
  IonButtons,
  IonIcon,
  IonCard,
  IonCardContent,
  useIonToast,
  IonSpinner,
  IonText,
  useIonAlert,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import { cloudUploadOutline, closeOutline, personAddOutline, mailOutline, checkmarkCircle, linkOutline } from 'ionicons/icons';
import { contacts, gmailAuth } from '../services/api.ts';
import { extractErrorMessage } from '../utils/errorUtils.ts';
import './Import.css';

interface ImportProps {
  isOpen: boolean;
  onClose: () => void;
  onContactAdded: () => void;
  gmailConnected: boolean;
  connectedEmail: string | null;
  gmailStatusLoading: boolean;
  onGmailStatusChange: () => void;
  initialMode?: 'quick' | 'gmail' | 'file';
}

const Import: React.FC<ImportProps> = ({
  isOpen,
  onClose,
  onContactAdded,
  gmailConnected,
  connectedEmail,
  gmailStatusLoading,
  onGmailStatusChange,
  initialMode = 'gmail'
}) => {
  const [present] = useIonToast();
  const [presentAlert] = useIonAlert();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<'quick' | 'gmail' | 'file'>(initialMode);
  const [quickAdd, setQuickAdd] = useState({
    name: '',
    email: '',
    company: '',
    title: '',
  });

  // Update active tab when initialMode changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialMode);
    }
  }, [isOpen, initialMode]);

  const handleQuickAdd = async () => {
    if (!quickAdd.name || !quickAdd.email) {
      present({
        message: 'Name and email are required',
        duration: 2000,
        color: 'danger'
      });
      return;
    }

    try {
      setLoading(true);
      // Send as array of one contact
      await contacts.importContacts([{
        name: quickAdd.name,
        email: quickAdd.email,
        company: quickAdd.company || '',
        title: quickAdd.title || '',
        source: 'manual_import'
      }]);
      
      onContactAdded();
      resetForm();
      present({
        message: 'Contact added successfully',
        duration: 2000,
        color: 'success'
      });
    } catch (error: any) {
      // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
      if (error.response?.status === 402) {
        setLoading(false);
        return;
      }
      
      const errorMessage = extractErrorMessage(error, 'Failed to add contact');
      if (errorMessage) {
        present({
          message: errorMessage,
          duration: 2000,
          color: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      present({
        message: 'Please upload a CSV or Excel file',
        duration: 2000,
        color: 'danger'
      });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      await contacts.uploadContactsFile(formData);
      
      onContactAdded();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      present({
        message: 'Contacts imported successfully',
        duration: 2000,
        color: 'success'
      });
    } catch (error: any) {
      // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
      if (error.response?.status === 402) {
        setLoading(false);
        return;
      }
      
      const errorMessage = extractErrorMessage(error, 'Failed to import contacts');
      if (errorMessage) {
        present({
          message: errorMessage,
          duration: 2000,
          color: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setQuickAdd({
      name: '',
      email: '',
      company: '',
      title: '',
    });
  };

  const downloadTemplate = () => {
    const csvContent = 'name,email,company,title\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleGmailConnect = async () => {
    try {
      setLoading(true);
      const response = await gmailAuth.authorize();
      // Redirect to Google OAuth
      window.location.href = response.auth_url;
    } catch (error: any) {
      setLoading(false);
      // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
      if (error.response?.status === 402) {
        return;
      }
      
      const errorMessage = extractErrorMessage(error, 'Failed to connect Gmail');
      if (errorMessage) {
        present({
          message: errorMessage,
          duration: 2000,
          color: 'danger'
        });
      }
    }
  };

  const handleGmailImport = async () => {
    setLoading(true);
    try {
      const response = await gmailAuth.importContacts();

      onContactAdded();
      present({
        message: `Successfully imported ${response.total_imported} contacts!`,
        duration: 3000,
        color: 'success'
      });
    } catch (error: any) {
      // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
      if (error.response?.status === 402) {
        setLoading(false);
        return;
      }
      
      const errorMessage = extractErrorMessage(error, 'Failed to import Gmail contacts');
      if (errorMessage) {
        present({
          message: errorMessage,
          duration: 2000,
          color: 'danger'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Import Contacts</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>

        <IonToolbar>
          <IonSegment
            value={activeTab}
            onIonChange={e => setActiveTab(e.detail.value as 'quick' | 'gmail' | 'file')}
          >
            <IonSegmentButton value="gmail">
              <IonLabel>Gmail</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="file">
              <IonLabel>File</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="quick">
              <IonLabel>Quick Add</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>

      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(6px)',
          zIndex: 100000,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <IonSpinner name="crescent" style={{ width: '56px', height: '56px' }} color="light" />
          <IonText color="light" style={{ fontSize: '17px', fontWeight: 500 }}>
            Processing...
          </IonText>
        </div>
      )}

      <IonContent className="ion-padding">

        {/* Gmail Import Section */}
        {activeTab === 'gmail' && (
          <IonCard>
            <IonCardContent>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: 500,
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center'
              }}>
                <IonIcon icon={mailOutline} style={{ marginRight: '0.5rem' }} />
                Import from Gmail
              </h2>

              {gmailStatusLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                  <IonSpinner name="crescent" />
                  <IonText color="medium" style={{ display: 'block', marginTop: '1rem' }}>
                    Checking Gmail connection...
                  </IonText>
                </div>
              ) : !gmailConnected ? (
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <IonText color="medium">
                    <p style={{ marginBottom: '1rem' }}>
                      Connect your Gmail to import contacts automatically
                    </p>
                  </IonText>
                  <IonButton
                    onClick={handleGmailConnect}
                    expand="block"
                    style={{
                      '--background': '#1e3a8a',
                      '--color': 'white',
                      borderRadius: '12px',
                      height: '48px',
                      fontWeight: 600,
                      fontSize: '16px',
                      marginBottom: '1rem',
                    }}
                  >
                    <IonIcon icon={linkOutline} slot="start" />
                    Connect Gmail
                  </IonButton>
                  <IonButton
                    onClick={() => {
                      presentAlert({
                        header: 'Remove All Gmail Contacts',
                        message: 'This will permanently delete all contacts that were imported from Gmail. This action cannot be undone.',
                        cssClass: 'custom-alert',
                        buttons: [
                          {
                            text: 'Cancel',
                            role: 'cancel',
                            cssClass: 'alert-button-cancel',
                          },
                          {
                            text: 'Remove All',
                            role: 'destructive',
                            cssClass: 'alert-button-remove',
                            handler: async () => {
                              setTimeout(async () => {
                                setLoading(true);
                                try {
                                  await gmailAuth.deleteGmailContacts();
                                  onContactAdded();
                                  present({
                                    message: 'All Gmail contacts removed.',
                                    duration: 2000,
                                    color: 'success'
                                  });
                                } catch (error: any) {
                                  // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
                                  if (error.response?.status === 402) {
                                    setLoading(false);
                                    return;
                                  }

                                  const errorMessage = extractErrorMessage(error, 'Failed to remove contacts');
                                  if (errorMessage) {
                                    present({
                                      message: errorMessage,
                                      duration: 2000,
                                      color: 'danger'
                                    });
                                  }
                                } finally {
                                  setLoading(false);
                                }
                              }, 100);
                            },
                          },
                        ],
                      });
                    }}
                    color="danger"
                    fill="outline"
                    expand="block"
                    size="small"
                    style={{
                      borderRadius: '12px',
                      height: '40px',
                      fontWeight: 500,
                      fontSize: '14px',
                    }}
                  >
                    Remove All Gmail Contacts
                  </IonButton>
                </div>
              ) : (
                <div>
                  <div className="gmail-connected-badge">
                    <IonIcon icon={checkmarkCircle} color="success" />
                    <IonText color="success">
                      <strong>{connectedEmail}</strong>
                    </IonText>
                  </div>
                  <IonButton
                    onClick={handleGmailImport}
                    color="success"
                    expand="block"
                    style={{
                      marginBottom: '0.75rem',
                      borderRadius: '12px',
                      height: '48px',
                      fontWeight: 600,
                      fontSize: '16px',
                    }}
                  >
                    <IonIcon icon={mailOutline} slot="start" />
                    Import Gmail Contacts
                  </IonButton>
                  <IonButton
                    onClick={() => {
                      presentAlert({
                        header: 'Disconnect Gmail',
                        message: 'Do you want to remove all contacts imported from this Gmail account?',
                        cssClass: 'custom-alert',
                        buttons: [
                          {
                            text: 'Cancel',
                            role: 'cancel',
                            cssClass: 'alert-button-cancel',
                          },
                          {
                            text: 'Keep Contacts',
                            cssClass: 'alert-button-keep',
                            handler: async () => {
                              setTimeout(async () => {
                                setLoading(true);
                                try {
                                  await gmailAuth.disconnect();
                                  onGmailStatusChange();
                                  present({
                                    message: 'Gmail disconnected. Contacts retained.',
                                    duration: 2000,
                                    color: 'success'
                                  });
                                } catch (error: any) {
                                  // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
                                  if (error.response?.status === 402) {
                                    setLoading(false);
                                    return;
                                  }

                                  const errorMessage = extractErrorMessage(error, 'Failed to disconnect Gmail');
                                  if (errorMessage) {
                                    present({
                                      message: errorMessage,
                                      duration: 2000,
                                      color: 'danger'
                                    });
                                  }
                                } finally {
                                  setLoading(false);
                                }
                              }, 100);
                            },
                          },
                          {
                            text: 'Remove Contacts',
                            role: 'destructive',
                            cssClass: 'alert-button-remove',
                            handler: async () => {
                              setTimeout(async () => {
                                setLoading(true);
                                try {
                                  await gmailAuth.deleteGmailContacts();
                                  await gmailAuth.disconnect();
                                  onGmailStatusChange();
                                  onContactAdded();
                                  present({
                                    message: 'Gmail disconnected and contacts removed.',
                                    duration: 2000,
                                    color: 'success'
                                  });
                                } catch (error: any) {
                                  // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
                                  if (error.response?.status === 402) {
                                    setLoading(false);
                                    return;
                                  }

                                  const errorMessage = extractErrorMessage(error, 'Failed to disconnect Gmail');
                                  if (errorMessage) {
                                    present({
                                      message: errorMessage,
                                      duration: 2000,
                                      color: 'danger'
                                    });
                                  }
                                } finally {
                                  setLoading(false);
                                }
                              }, 100);
                            },
                          },
                        ],
                      });
                    }}
                    color="medium"
                    fill="outline"
                    expand="block"
                    size="small"
                    style={{
                      borderRadius: '12px',
                      height: '44px',
                      fontWeight: 500,
                    }}
                  >
                    Disconnect Gmail
                  </IonButton>
                </div>
              )}
            </IonCardContent>
          </IonCard>
        )}

        {/* Quick Add Section */}
        {activeTab === 'quick' && (
          <IonCard>
          <IonCardContent>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 500,
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              <IonIcon icon={personAddOutline} style={{ marginRight: '0.5rem' }} />
              Quick Add Contact
            </h2>
            
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Name *</IonLabel>
                <IonInput
                  value={quickAdd.name}
                  onIonInput={e => setQuickAdd({ ...quickAdd, name: e.detail.value! })}
                  placeholder="John Doe"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Email *</IonLabel>
                <IonInput
                  type="email"
                  value={quickAdd.email}
                  onIonInput={e => setQuickAdd({ ...quickAdd, email: e.detail.value! })}
                  placeholder="john@company.com"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Company</IonLabel>
                <IonInput
                  value={quickAdd.company}
                  onIonInput={e => setQuickAdd({ ...quickAdd, company: e.detail.value! })}
                  placeholder="Company Name"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Title</IonLabel>
                <IonInput
                  value={quickAdd.title}
                  onIonInput={e => setQuickAdd({ ...quickAdd, title: e.detail.value! })}
                  placeholder="Software Engineer"
                />
              </IonItem>
            </IonList>

            <IonButton
              expand="block"
              onClick={handleQuickAdd}
              style={{
                '--background': '#1e3a8a',
                '--color': 'white',
                marginTop: '1rem',
                borderRadius: '12px',
                height: '48px',
                fontWeight: 600,
                fontSize: '16px',
              }}
            >
              Add Contact
            </IonButton>
          </IonCardContent>
        </IonCard>
        )}

        {/* File Upload Section */}
        {activeTab === 'file' && (
        <IonCard>
          <IonCardContent>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: 500,
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              <IonIcon icon={cloudUploadOutline} style={{ marginRight: '0.5rem' }} />
              Upload File
            </h2>

            <div className="file-upload-area">
              <p>
                Drag and drop your CSV or Excel file here
              </p>
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv,.xlsx"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <IonButton
                onClick={() => fileInputRef.current?.click()}
                style={{
                  '--background': '#1e3a8a',
                  '--color': 'white',
                  borderRadius: '12px',
                  height: '48px',
                  fontWeight: 600,
                  fontSize: '16px',
                }}
              >
                Choose File
              </IonButton>
              <p style={{ 
                fontSize: '0.875rem',
                color: '#666',
                marginTop: '1rem'
              }}>
                Supported formats: .csv, .xlsx
              </p>
            </div>

            <div style={{ 
              marginTop: '1rem',
              textAlign: 'center'
            }}>
              <IonButton 
                fill="clear" 
                size="small"
                onClick={downloadTemplate}
              >
                Download template file
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>
        )}
      </IonContent>
    </IonModal>
  );
};

export default Import;